import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {forkJoin} from 'rxjs';
import {CompanyService} from '@services/company.service';
import {ICompanyDetailsSimplified, ITaxCalendar} from '@models/company.interface';
import {ICompteAdministration} from '@models/compteAdministration.interface';
import {AlertCircle, Calendar, Clock, Landmark, LucideAngularModule} from '@shared/components/icons/google-icons';

interface ClientFiscalRow {
  company: ICompanyDetailsSimplified;
  detail: ICompteAdministration;
  taxCalendar: ITaxCalendar[];
}

@Component({
  selector: 'app-expert-fiscal-tracking',
  imports: [DatePipe, LucideAngularModule],
  templateUrl: './expert-fiscal-tracking.html',
})
export class ExpertFiscalTracking implements OnInit {
  private readonly companyService = inject(CompanyService);

  loading = signal(true);
  rows: WritableSignal<ClientFiscalRow[]> = signal([]);
  expandedClient = signal<string | null>(null);

  protected readonly Landmark = Landmark;
  protected readonly Calendar = Calendar;
  protected readonly AlertCircle = AlertCircle;
  protected readonly Clock = Clock;

  ngOnInit(): void {
    this.companyService.getCompanies().subscribe({
      next: (companies) => {
        if (!companies.length) {
          this.rows.set([]);
          this.loading.set(false);
          return;
        }
        forkJoin(companies.map((company) => this.companyService.getCompanyBySiret(company.siret))).subscribe({
          next: (details) => {
            this.rows.set(details.map((detail, index) => ({
              company: companies[index],
              detail,
              taxCalendar: detail.company?.taxCalendar ?? [],
            })));
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
            this.rows.set([]);
          },
        });
      },
      error: () => {
        this.loading.set(false);
        this.rows.set([]);
      },
    });
  }

  hasConfiguredCredentials(detail: ICompteAdministration): boolean {
    return detail.mdpSecretImpotsGouv != null;
  }

  toggleExpanded(siret: string): void {
    this.expandedClient.update((current) => current === siret ? null : siret);
  }

  isExpanded(siret: string): boolean {
    return this.expandedClient() === siret;
  }
}
